import clsx from "clsx";
import Modal from "../Modal/Modal";
import RadioGroup from "../RadioGroup/RadioGroup";
import CollapsibleBlock from "../CollapsibleBlock/CollapsibleBlock";
import CheckBox from "../CheckBox/CheckBox";
import { ICON_SIZES } from "src/helpers/constants";
import { useTranslation } from "react-i18next";
import { MdClose } from "react-icons/md";
import useModalsStore from "src/store/modalsStore";
import useCollapsibles, {
  ETablePreferencesMobileCollapsibles,
} from "src/hooks/useCollapsibles";
import useMobile from "src/hooks/useMobile";
import useTablePreferences, {
  EPaginationStyle,
} from "src/hooks/useTablePreferences";
import { EButtonKind } from "../Button/ButtonTypes";

const TableSettingsModal: React.FC = () => {
  const { t } = useTranslation();
  const { isMobile } = useMobile();
  const {
    allowMultipleSelection,
    allowPinnedColumns,
    hasBottomPagination,
    hasDoublePaginationEnabled,
    showColumnFilters,
    hasTopPagination,
    selectOnRowClick,
    updateTablePreferences,
    paginationStyle,
  } = useTablePreferences();
  const {
    collapsiblePreferences,
    updateCollapsible,
    isTablePreferencesMobileCollapsibleOpen,
    toggleTablePreferencesMobileCollapsible,
  } = useCollapsibles();

  const { setShowTableSettingsModal } = useModalsStore();

  return (
    <Modal
      onEscape={() => setShowTableSettingsModal(false)}
      className={clsx(["!w-full", "md:!min-w-[600px]", "md:!max-w-[600px]"])}
    >
      <Modal.Header
        onClose={() => setShowTableSettingsModal(false)}
        title={t("customize_tables.title")}
      />
      <Modal.Body>
        <div className={clsx(["flex", "flex-col", "gap-4", "w-full", "pb-2"])}>
          <div className={clsx(["text-sm", "md:text-base"])}>
            {t("customize_tables.description")}
          </div>
          <CollapsibleBlock
            title={t("customize_tables.collapsibles.style.title")}
            open={
              isMobile
                ? isTablePreferencesMobileCollapsibleOpen(
                    ETablePreferencesMobileCollapsibles.PAGINATION
                  )
                : collapsiblePreferences.tablePreferencesPaginationOpen
            }
            onChange={(isOpen: boolean) => {
              if (isMobile) {
                toggleTablePreferencesMobileCollapsible(
                  ETablePreferencesMobileCollapsibles.PAGINATION
                );
              } else {
                updateCollapsible({ tablePreferencesPaginationOpen: isOpen });
              }
            }}
          >
            <RadioGroup
              layout="vertical"
              onChange={(value: any) =>
                updateTablePreferences({ paginationStyle: value as any })
              }
              selected={paginationStyle}
              options={[
                {
                  label: t("customize_tables.collapsibles.style.top"),
                  description: t(
                    "customize_tables.collapsibles.style.top_description"
                  ),
                  value: EPaginationStyle.TOP,
                },
                {
                  label: t("customize_tables.collapsibles.style.bottom"),
                  description: t(
                    "customize_tables.collapsibles.style.bottom_description"
                  ),
                  value: EPaginationStyle.BOTTOM,
                },
                {
                  label: t("customize_tables.collapsibles.style.double"),
                  description: t(
                    "customize_tables.collapsibles.style.double_description"
                  ),
                  value: EPaginationStyle.DOUBLE,
                },
              ]}
            />
          </CollapsibleBlock>
          <CollapsibleBlock
            title={t("customize_tables.collapsibles.columns.title")}
            open={
              isMobile
                ? isTablePreferencesMobileCollapsibleOpen(
                    ETablePreferencesMobileCollapsibles.COLUMNS
                  )
                : collapsiblePreferences.tablePreferencesColumnsOpen
            }
            onChange={(isOpen: boolean) => {
              if (isMobile) {
                toggleTablePreferencesMobileCollapsible(
                  ETablePreferencesMobileCollapsibles.COLUMNS
                );
              } else {
                updateCollapsible({ tablePreferencesColumnsOpen: isOpen });
              }
            }}
          >
            <div className={clsx(["flex", "w-full", "gap-4", "flex-col"])}>
              <CheckBox
                checked={allowPinnedColumns}
                onChange={(value) =>
                  updateTablePreferences({ allowPinnedColumns: value })
                }
                label={t("customize_tables.collapsibles.columns.fixed_columns")}
                description={t(
                  "customize_tables.collapsibles.columns.fixed_columns_description"
                )}
              />
              <CheckBox
                checked={showColumnFilters}
                onChange={(value) =>
                  updateTablePreferences({ showColumnFilters: value })
                }
                label={t(
                  "customize_tables.collapsibles.columns.columns_filter"
                )}
                description={t(
                  "customize_tables.collapsibles.columns.columns_filter_description"
                )}
              />
            </div>
          </CollapsibleBlock>
          <CollapsibleBlock
            title={t("customize_tables.collapsibles.click.title")}
            open={
              isMobile
                ? isTablePreferencesMobileCollapsibleOpen(
                    ETablePreferencesMobileCollapsibles.SELECTION
                  )
                : collapsiblePreferences.tablePreferencesSelectionOpen
            }
            onChange={(isOpen: boolean) => {
              if (isMobile) {
                toggleTablePreferencesMobileCollapsible(
                  ETablePreferencesMobileCollapsibles.SELECTION
                );
              } else {
                updateCollapsible({ tablePreferencesSelectionOpen: isOpen });
              }
            }}
          >
            <div className={clsx(["flex", "w-full", "gap-4", "flex-col"])}>
              <CheckBox
                checked={selectOnRowClick}
                onChange={(value) =>
                  updateTablePreferences({ selectionOnClickAnyCell: value })
                }
                label={t("customize_tables.collapsibles.click.select_cell")}
                description={t(
                  "customize_tables.collapsibles.click.select_cell_description"
                )}
              />
              <CheckBox
                checked={allowMultipleSelection}
                onChange={(value) =>
                  updateTablePreferences({ disableMultipleSelection: !value })
                }
                label={t(
                  "customize_tables.collapsibles.click.allow_multiple_selection"
                )}
                description={t(
                  "customize_tables.collapsibles.click.allow_multiple_selection_description"
                )}
              />
            </div>
          </CollapsibleBlock>
        </div>
      </Modal.Body>
      <Modal.Footer
        actions={[
          {
            text: t("common_buttons.close"),
            onClick: () => setShowTableSettingsModal(false),
            kind: EButtonKind.NORMAL,
            icon: <MdClose size={ICON_SIZES.MD} />,
          },
        ]}
      />
    </Modal>
  );
};

export default TableSettingsModal;
